import {
  EnvironmentFilled,
  InstagramOutlined,
  TikTokOutlined,
} from "@ant-design/icons";
import {
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Switch,
  Typography,
} from "antd";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPlaceById,
  getPlacesByName,
  getSuggestedSocialMedia,
} from "services/SauceService";
import WeekHoursField from "pages/collab/WeekHoursField";
import { useSelector } from "react-redux";
import { useWatch } from "antd/es/form/Form";
import { BusinessState } from "redux-context/business";
import { isOpen24By7, parseGoogleOpeningHours } from "helpers/working-hours";
import bookingAvailability from "pages/collab/weekHoursDefault.json";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import PointOfContactFields from "components/point-of-contact-fields/PointOfContactFields";
import { ReactComponent as OpenTableIcon } from "assets/images/open-table-icon.svg";

interface VenueFormFieldsProps {
  namePath?: (string | number)[];
  formListNameFullPath?: (string | number)[];
  business: any;
  initialSearch?: string;
  open?: boolean;
  hiddenCopyWorkingHours?: boolean;
  newSocialMediaFields?: boolean;
}

export const VenueFormFields: React.FC<VenueFormFieldsProps> = ({
  namePath = [],
  business,
  formListNameFullPath = [],
  hiddenCopyWorkingHours = false,
  newSocialMediaFields = false,
}) => {
  const [loadingLocation, setLoadingLocation] = useState<boolean>(false);
  const [places, setPlaces] = useState<any[]>([]);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const form = useFormInstance();
  const { business: loggedBusiness } = useSelector(
    (state: BusinessState) => state.business,
  );
  const instagramAccounts = useMemo(() => {
    return (
      loggedBusiness?.socialMediasConnected
        ?.filter((socialMedia) =>
          ["instagram", "meta"].includes(
            socialMedia?.socialMediaName?.toLowerCase(),
          ),
        )
        ?.map((metaAccount) => ({
          value: metaAccount.accountName,
          label: metaAccount.accountName,
        })) || []
    );
  }, [loggedBusiness]);

  const tiktokAccounts = useMemo(() => {
    return (
      loggedBusiness?.socialMediasConnected
        ?.filter(
          (socialMedia) =>
            socialMedia?.socialMediaName?.toLowerCase() === "tiktok",
        )
        ?.map((tiktokAccount) => ({
          value: tiktokAccount.accountName,
          label: tiktokAccount.accountName,
        })) || []
    );
  }, [loggedBusiness]);

  const hasInstagramAccounts = instagramAccounts.length > 0;
  const hasTikTokAccounts = tiktokAccounts.length > 0;
  const showAssociatedSocialMedia = hasInstagramAccounts || hasTikTokAccounts;
  const socialMediaColumnSpan =
    hasInstagramAccounts && hasTikTokAccounts ? 12 : 24;

  const getFieldName = (field: string) => {
    return namePath.length > 0 ? [...namePath, field] : [field];
  };

  const getFullFieldPath = (field: string) => {
    if (formListNameFullPath.length > 0 && namePath.length > 0) {
      return [...formListNameFullPath, ...namePath, field];
    }
    return getFieldName(field);
  };

  const googlePlaceIdFieldName = getFullFieldPath("googlePlaceId");
  const venueNameFieldName = getFullFieldPath("name");
  const googlePlaceIdNameFormattedFieldName = getFullFieldPath(
    "googlePlaceIdNameFormatted",
  );
  const googlePlaceId = useWatch(googlePlaceIdFieldName, form);
  const venueName = useWatch(venueNameFieldName, form);
  const googlePlaceIdNameFormatted = useWatch(
    googlePlaceIdNameFormattedFieldName,
    form,
  );
  const instagramUserName = useWatch(
    getFullFieldPath("instagramUserName"),
    form,
  );
  const tiktokUserName = useWatch(getFullFieldPath("tiktokUserName"), form);

  const venueWorkingHours = useWatch(
    getFullFieldPath("venueWorkingHours"),
    form,
  );

  const getLocation = useCallback(async (locationName: string) => {
    setLoadingLocation(true);
    const response: any = await getPlacesByName({ name: locationName });
    setPlaces(
      response.map((place: any) => ({
        value: place.place_id,
        label: `${place.name} - ${place.formatted_address}`,
        ...place,
      })),
    );
    setLoadingLocation(false);
  }, []);

  const handleSearch = (newValue: string) => {
    setSearchValue(newValue);
    if (newValue && newValue.trim()) {
      setPlaces([]);
      getLocation(newValue);
      setLocationDropdownOpen(true);
    } else {
      setLocationDropdownOpen(false);
      setPlaces([]);
    }
  };

  const handleChange = async (_: string, option: any) => {
    if (!option) {
      console.log("option", option);
      setSearchValue("");
      setLocationDropdownOpen(false);
      setPlaces([]);
      return;
    }
    form.setFieldValue(
      getFullFieldPath("addressDisplay"),
      option.formatted_address,
    );
    form.setFieldValue(
      getFullFieldPath("googlePlaceIdNameFormatted"),
      option.label,
    );
    const response: any = await getPlaceById(option.place_id, true);
    const isAlwaysOpen = isOpen24By7(response?.opening_hours);
    const venueWorkingHours = isAlwaysOpen
      ? bookingAvailability
      : parseGoogleOpeningHours(response?.opening_hours);
    form.setFieldValue(
      getFullFieldPath("venueWorkingHours"),
      venueWorkingHours,
    );
    form.setFieldValue(getFullFieldPath("name"), option.name);
    form.setFieldValue(
      getFullFieldPath("placeLongitude"),
      option?.geometry?.location?.lng,
    );
    form.setFieldValue(
      getFullFieldPath("placeLatitude"),
      option?.geometry?.location?.lat,
    );
  };

  const handleClear = () => {
    setSearchValue("");
    setLocationDropdownOpen(false);
    setPlaces([]);
  };

  const handleDropdownVisibleChange = (visible: boolean) => {
    if (!visible) {
      setLocationDropdownOpen(false);
    } else {
      if (searchValue.trim().length > 0) {
        setLocationDropdownOpen(true);
      } else {
        setLocationDropdownOpen(false);
      }
    }
  };

  const renderOption = (option: any) => {
    return (
      <div style={{ padding: "8px", cursor: "pointer" }}>
        {`${option.data.name} - ${option.data.formatted_address}`}
      </div>
    );
  };

  const getNotFoundContent = () => {
    if (loadingLocation) {
      return "Searching...";
    }
    if (places.length === 0 && searchValue.trim().length > 0) {
      return "No results found";
    }
    return null;
  };

  const handlerSuggestSocialMedia = async () => {
    if (
      !venueName ||
      !newSocialMediaFields ||
      !showAssociatedSocialMedia ||
      (instagramUserName && tiktokUserName)
    )
      return;

    const response: any = await getSuggestedSocialMedia({
      businessId: business._id,
      placeName: venueName,
    });

    if (hasInstagramAccounts && !instagramUserName) {
      form.setFieldValue(
        getFullFieldPath("instagramUserName"),
        response.instagramHandle,
      );
    }

    if (hasTikTokAccounts && !tiktokUserName) {
      form.setFieldValue(
        getFullFieldPath("tiktokUserName"),
        response.tiktokHandle,
      );
    }
  };

  useEffect(() => {
    if (!venueWorkingHours || venueWorkingHours?.some((day: any) => day.open))
      return;

    message.error("Please let at least one day open");
  }, [venueWorkingHours]);

  return (
    <>
      <Form.Item name={getFieldName("placeLongitude")} hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={getFieldName("placeLatitude")} hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={getFieldName("googlePlaceIdNameFormatted")} hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item
        name={getFieldName("googlePlaceId")}
        rules={[{ required: true, message: "Location is required" }]}
      >
        <Select
          placeholder="Type the complete business address"
          showSearch
          filterOption={false}
          onSearch={debounce(handleSearch, 500)}
          onSelect={handleChange}
          options={places}
          loading={loadingLocation}
          notFoundContent={getNotFoundContent()}
          allowClear={!!googlePlaceId}
          onClear={handleClear}
          suffixIcon={
            !googlePlaceId ? (
              <EnvironmentFilled style={{ color: "#9ca3af" }} />
            ) : undefined
          }
          open={locationDropdownOpen && searchValue.trim().length > 0}
          onOpenChange={handleDropdownVisibleChange}
          optionRender={renderOption}
          onBlur={handlerSuggestSocialMedia}
          labelRender={() => googlePlaceIdNameFormatted}
        />
      </Form.Item>

      <Form.Item
        label="Location Name"
        name={getFieldName("name")}
        style={{ marginBottom: 0 }}
        rules={[{ required: true, message: "Location name is required" }]}
      >
        <Input placeholder="Name of the location" autoComplete="off" />
      </Form.Item>

      <div
        style={{
          backgroundColor: "#F9F5ED",
          marginBottom: 24,
          height: 40,
          marginTop: -4,
          borderRadius: "0 0 6px 6px",
          display: "flex",
          alignItems: "center",
          border: "1px solid #E6E6E6",
          padding: "8px 16px",
          gap: 8,
        }}
      >
        <Form.Item name={getFieldName("ghostKitchen")} noStyle>
          <Switch />
        </Form.Item>
        I don't have a physical location
      </div>
      {newSocialMediaFields && showAssociatedSocialMedia && (
        <>
          <Typography.Text
            style={{ paddingBottom: 8, display: "block", fontWeight: 500 }}
          >
            Associated Social Media
          </Typography.Text>
          <Row gutter={[12, 12]}>
            {hasInstagramAccounts && (
              <Col span={socialMediaColumnSpan}>
                <Form.Item
                  label="Instagram"
                  name={getFieldName("instagramUserName")}
                >
                  <Select
                    placeholder="Select an Instagram account"
                    showSearch
                    allowClear
                    options={instagramAccounts}
                    optionFilterProp="label"
                    prefix={"@"}
                  />
                </Form.Item>
              </Col>
            )}
            {hasTikTokAccounts && (
              <Col span={socialMediaColumnSpan}>
                <Form.Item label="TikTok" name={getFieldName("tikTokUserName")}>
                  <Select
                    placeholder="Select a TikTok account"
                    showSearch
                    allowClear
                    options={tiktokAccounts}
                    optionFilterProp="label"
                    prefix={"@"}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </>
      )}
      {!newSocialMediaFields && (
        <>
          <Form.Item label="Connect social media" name="instagramUrl">
            <Input placeholder="Instagram" prefix={<InstagramOutlined />} />
          </Form.Item>
          <Form.Item name="tiktokUrl">
            <Input placeholder="TikTok" prefix={<TikTokOutlined />} />
          </Form.Item>
          <Form.Item
            name="openTableUrl"
            rules={[
              {
                validator: (_, value) => {
                  const re =
                    /^https:\/\/www\.opentable\.com\/booking\/restref\/availability\?(?=.*\bcorrelationId=[a-f0-9-]{36}\b)(?=.*\brestRef=\d+\b).*/i;

                  if (!value) return Promise.resolve();
                  return re.test(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Invalid OpenTable URL"));
                },
              },
            ]}
          >
            <Input
              placeholder="OpenTable booking widget URL"
              prefix={<OpenTableIcon />}
            />
          </Form.Item>
        </>
      )}
      <PointOfContactFields namePath={[...namePath, "pocs"]} />
      <Typography.Text style={{ paddingBottom: 8, display: "block" }}>
        When Do You Want Influencers to Visit You
      </Typography.Text>
      {!hiddenCopyWorkingHours && (
        <Form.Item label="Copy working hours" style={{ marginBottom: 16 }}>
          <Select
            placeholder="Select a venue to copy working hours"
            allowClear
            options={(business?.venues || []).map((venue: any) => ({
              value: venue._id,
              label: venue.name,
              venueWorkingHours: venue.venueWorkingHours,
            }))}
            onSelect={(_id, option) => {
              if (option.venueWorkingHours) {
                form.setFieldValue(
                  getFieldName("venueWorkingHours"),
                  option.venueWorkingHours,
                );
              }
            }}
          />
        </Form.Item>
      )}
      <WeekHoursField
        name={[...getFieldName("venueWorkingHours")]}
        prefixName={formListNameFullPath}
      />
    </>
  );
};

export default VenueFormFields;
